import { useCallback, useEffect, useState, useMemo } from 'react';
import { resolveId } from '@medplum/core';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { AccessPolicy, ProjectMembership } from '@medplum/fhirtypes';
import { POLICY_CONFIGS, adminPolicy } from './access-policies';

// -----------------------------------------------------------------------------
const SUPER_ADMIN_EMAILS = ['admin@example.com'];

// -----------------------------------------------------------------------------
export interface ComponentAccess {
  // LIMS Components
  canAccessLims: boolean;
  canCreateOrders: boolean;
  canEditOrders: boolean;
  canDeleteOrders: boolean;
  canManageSpecimens: boolean;
  canViewDiagnosticReports: boolean;
  canCreateDiagnosticReports: boolean;
  canReviewResults: boolean;
  canManageConsent: boolean;
  canViewEncounters: boolean;

  // Admin Components
  canAccessAdmin: boolean;
  canManageUsers: boolean;
  canManageAccessPolicies: boolean;
  canViewSystemStats: boolean;

  // Patient Management
  canAccessPatients: boolean;
  canCreatePatients: boolean;
  canEditPatients: boolean;
  canViewPatientHistory: boolean;
  canManageEncounters: boolean;

  // Tasks & Workflow
  canAccessTasks: boolean;
  canClaimTasks: boolean;
  canAssignTasks: boolean;
  canCompleteTasksForOthers: boolean;
  // Practitioner Management
  canAccessPractitioners: boolean;
  canViewPractitioners: boolean;
  canManagePractitioners: boolean;

  // Payment & Billing
  canAccessPayments: boolean;
  canManageChargeItems: boolean;
  canManageInvoices: boolean;
  canViewBillingAccounts: boolean;

  // Search
  canAccessSearch: boolean;

  // General Permissions
  canViewReports: boolean;
  canExportData: boolean;
  canManageDocuments: boolean;
}

// -----------------------------------------------------------------------------
export interface UseCurrentAccessPolicyReturn {
  accessPolicy: AccessPolicy | null;
  membership: ProjectMembership | null;
  componentAccess: ComponentAccess;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Resolves the config key from the access policy name.
const resolvePolicyConfigKey = (
  accessPolicy: AccessPolicy | null
): string | undefined => {
  const name = accessPolicy?.name?.toLowerCase();
  if (name && name in POLICY_CONFIGS) {
    return name;
  }
  return undefined;
};

// -----------------------------------------------------------------------------
const DEFAULT_COMPONENT_ACCESS: ComponentAccess = {
  // LIMS Components - default restrictive access
  canAccessLims: false,
  canCreateOrders: false,
  canEditOrders: false,
  canDeleteOrders: false,
  canManageSpecimens: false,
  canViewDiagnosticReports: false,
  canCreateDiagnosticReports: false,
  canReviewResults: false,
  canManageConsent: false,
  canViewEncounters: false,

  // Admin Components - default no admin access
  canAccessAdmin: false,
  canManageUsers: false,
  canManageAccessPolicies: false,
  canViewSystemStats: false,

  // Patient Management - basic read access
  canAccessPatients: true,
  canCreatePatients: false,
  canEditPatients: false,
  canViewPatientHistory: true,
  canManageEncounters: false,

  // Tasks & Workflow - basic task access
  canAccessTasks: true,
  canClaimTasks: true,
  canAssignTasks: false,
  canCompleteTasksForOthers: false,
  // Practitioner Management - default no access
  canAccessPractitioners: false,
  canViewPractitioners: false,
  canManagePractitioners: false,

  // Payment & Billing - default no access
  canAccessPayments: false,
  canManageChargeItems: false,
  canManageInvoices: false,
  canViewBillingAccounts: false,

  // Search
  canAccessSearch: false,

  // General Permissions - basic access
  canViewReports: true,
  canExportData: false,
  canManageDocuments: false,
};

// -----------------------------------------------------------------------------
/**
 * Determines component access based on access policy rules and resource permissions
 * @param accessPolicy - The user's access policy defining resource permissions
 * @param userIsAdmin - Whether the user has admin privileges
 * @returns ComponentAccess object with boolean flags for each permission
 */
const determineComponentAccess = (
  accessPolicy: AccessPolicy | null,
  userIsAdmin: boolean
): ComponentAccess => {
  // Admin users get full access to everything
  if (userIsAdmin) {
    return { ...adminPolicy };
  }

  // Look up the policy config by name
  const configKey = resolvePolicyConfigKey(accessPolicy);
  if (configKey) {
    return { ...POLICY_CONFIGS[configKey] };
  }

  // No matching config — return default restrictive permissions
  return { ...DEFAULT_COMPONENT_ACCESS };
};

// -----------------------------------------------------------------------------
/**
 * Hook to fetch and manage the current user's access policy and component permissions
 * @returns Object containing access policy, membership, component permissions, and utility functions
 */
export const useCurrentAccessPolicy = (): UseCurrentAccessPolicyReturn => {
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  const [accessPolicy, setAccessPolicy] = useState<AccessPolicy | null>(null);
  const [membership, setMembership] = useState<ProjectMembership | null>(null);
  const [componentAccess, setComponentAccess] = useState<ComponentAccess>(
    DEFAULT_COMPONENT_ACCESS
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -----------------------------------------------------------------------------
  const loadAccessPolicy = useCallback(async () => {
    if (!profile) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current login information
      const activeLogin = medplum.getActiveLogin();
      if (!activeLogin) {
        throw new Error('No active login found');
      }

      const currentMembership = medplum.getProjectMembership() || null;
      setMembership(currentMembership);

      let userAccessPolicy: AccessPolicy | null = null;

      try {
        // The merged policy from the SDK strips the `name` field.
        // Fetch the raw policy by ID so we can match against POLICY_CONFIGS.
        const mergedPolicy = medplum.getAccessPolicy() || null;
        const policyId = resolveId(mergedPolicy?.basedOn?.[0]);

        if (policyId) {
          try {
            userAccessPolicy = await medplum.readResource(
              'AccessPolicy',
              policyId
            );
          } catch {
            userAccessPolicy = mergedPolicy;
          }
        } else {
          userAccessPolicy = mergedPolicy;
        }

        setAccessPolicy(userAccessPolicy);
      } catch (accessPolicyError) {
        console.warn('Failed to load access policy:', accessPolicyError);
        // Continue without access policy - user will get default permissions
      }

      // Determine component access based on access policy and membership
      const userIsAdmin =
        currentMembership?.admin === true ||
        SUPER_ADMIN_EMAILS.includes(currentMembership?.user?.display || '');
      const calculatedAccess = determineComponentAccess(
        userAccessPolicy,
        userIsAdmin
      );

      setComponentAccess(calculatedAccess);
    } catch (err) {
      console.error('Error loading access policy:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load access policy';
      setError(errorMessage);

      // Reset to default permissions on error
      setAccessPolicy(null);
      setMembership(null);
      setComponentAccess(DEFAULT_COMPONENT_ACCESS);
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  // -----------------------------------------------------------------------------
  useEffect(() => {
    loadAccessPolicy();
  }, [loadAccessPolicy]);
  // Memoize componentAccess to prevent unnecessary re-renders
  const memoizedComponentAccess = useMemo(
    () => componentAccess,
    [componentAccess]
  );

  // Memoize isAdmin to prevent unnecessary re-renders
  const memoizedIsAdmin = useMemo(
    () => membership?.admin === true,
    [membership?.admin]
  );

  // -----------------------------------------------------------------------------
  return {
    accessPolicy,
    membership,
    componentAccess: memoizedComponentAccess,
    isAdmin: memoizedIsAdmin,
    loading,
    error,
    refetch: loadAccessPolicy,
  };
};
